import { useState } from 'react';
import { toast } from 'sonner';
import {
  useShifts,
  useCreateShift,
  useUpdateShift,
  useDeleteShift,
} from '../../../services/httpServices/queries';
import type { Shift } from '../../../services/httpServices/hrService';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { TimePicker } from '../../../components/ui/DatePicker';
import { SHIFT_COLORS, formatShiftTime, generateShiftCode, shiftColorStyle } from './utils';
import { CalendarClock, Check, Clock, Edit2, Loader2, Moon, Plus, RefreshCw, Trash2, X } from 'lucide-react';

interface ShiftFormState {
  shiftCode: string;
  name: string;
  startTime: string;
  endTime: string;
  color: string;
  description: string;
  breakMinutes: number;
  displayOrder: number;
  isActive: boolean;
}

const EMPTY_FORM: ShiftFormState = {
  shiftCode: '',
  name: '',
  startTime: '08:00',
  endTime: '16:00',
  color: SHIFT_COLORS[0],
  description: '',
  breakMinutes: 0,
  displayOrder: 0,
  isActive: true,
};

export function ShiftManagement() {
  const { data: shiftsData, isLoading, error } = useShifts();
  const createShift = useCreateShift();
  const updateShift = useUpdateShift();
  const deleteShift = useDeleteShift();

  const shifts = shiftsData?.data || [];

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Shift | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Shift | null>(null);
  const [form, setForm] = useState<ShiftFormState>(EMPTY_FORM);
  // Tracks whether the user manually changed the code; while false the code is
  // auto-generated from the shift name.
  const [codeEdited, setCodeEdited] = useState(false);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setCodeEdited(false);
    setShowForm(false);
    setEditing(null);
  };

  const handleNameChange = (name: string) => {
    setForm((prev) => ({
      ...prev,
      name,
      shiftCode: codeEdited ? prev.shiftCode : generateShiftCode(name),
    }));
  };

  const regenerateCode = () => {
    setForm((prev) => ({ ...prev, shiftCode: generateShiftCode(prev.name) }));
    setCodeEdited(false);
  };

  const startEdit = (shift: Shift) => {
    setEditing(shift);
    setForm({
      shiftCode: shift.shiftCode,
      name: shift.name,
      startTime: shift.startTime.slice(0, 5),
      endTime: shift.endTime.slice(0, 5),
      color: shift.color || SHIFT_COLORS[0],
      description: shift.description || '',
      breakMinutes: shift.breakMinutes ?? 0,
      displayOrder: shift.displayOrder ?? 0,
      isActive: shift.isActive,
    });
    setCodeEdited(true);
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...form,
      shiftCode: form.shiftCode.trim().toUpperCase().replace(/\s+/g, '_'),
      name: form.name.trim(),
      breakMinutes: Number(form.breakMinutes) || 0,
      displayOrder: Number(form.displayOrder) || 0,
    };
    if (editing) {
      updateShift.mutate(
        { id: editing.id, data: payload },
        {
          onSuccess: () => {
            toast.success('Shift updated');
            resetForm();
          },
          onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to update shift'),
        }
      );
    } else {
      createShift.mutate(payload, {
        onSuccess: () => {
          toast.success('Shift created');
          resetForm();
        },
        onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to create shift'),
      });
    }
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteShift.mutate(pendingDelete.id, {
      onSuccess: () => {
        toast.success('Shift deleted');
        setPendingDelete(null);
      },
      onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to delete shift'),
    });
  };

  const isSaving = createShift.isPending || updateShift.isPending;

  const duration = (() => {
    const [startHour, startMinute] = form.startTime.split(':').map(Number);
    const [endHour, endMinute] = form.endTime.split(':').map(Number);
    if ([startHour, startMinute, endHour, endMinute].some((value) => Number.isNaN(value))) {
      return '';
    }
    let minutes = endHour * 60 + endMinute - (startHour * 60 + startMinute);
    if (minutes <= 0) minutes += 24 * 60;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return `${hours}h${rest ? ` ${rest}m` : ''}`;
  })();
  const isOvernight = form.endTime <= form.startTime;

  const inputClass =
    'w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-text outline-none transition placeholder:text-text-muted/70 focus:ring-2 focus:ring-primary/40';
  const labelClass = 'mb-1.5 block text-xs font-medium text-text';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text">Shift Master</h2>
          <p className="text-sm text-text-muted">
            Create the shifts your facility uses. These appear as selectable options when assigning duties.
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          New Shift
        </button>
      </div>

      {error && (
        <div className="p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
          Failed to load shifts
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Shift</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Hours</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Break</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {shifts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                      No shifts yet. Create your first shift to start building the roster.
                    </td>
                  </tr>
                ) : (
                  shifts.map((shift) => (
                    <tr key={shift.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span
                            className="w-9 h-9 rounded-lg flex items-center justify-center border"
                            style={shiftColorStyle(shift.color)}
                          >
                            <CalendarClock size={16} />
                          </span>
                          <div>
                            <p className="text-sm font-medium text-text">{shift.name}</p>
                            {shift.description && (
                              <p className="text-xs text-text-muted line-clamp-1">{shift.description}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted font-mono">{shift.shiftCode}</td>
                      <td className="px-4 py-3 text-sm text-text whitespace-nowrap">
                        {formatShiftTime(shift.startTime)} - {formatShiftTime(shift.endTime)}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">{shift.breakMinutes || 0} min</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            shift.isActive ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                          }`}
                        >
                          {shift.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => startEdit(shift)}
                            className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-primary transition-colors"
                            title="Edit shift"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => setPendingDelete(shift)}
                            className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-danger transition-colors"
                            title="Delete shift"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={resetForm}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 border-b border-border bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-4 py-3.5">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-white shadow-sm">
                  <CalendarClock size={17} />
                </div>
                <div className="min-w-0">
                  <h2 className="truncate text-base font-semibold leading-tight text-text">
                    {editing ? 'Edit Shift' : 'New Shift'}
                  </h2>
                  <p className="mt-0.5 truncate text-xs text-text-muted">
                    {editing
                      ? 'Update hours, colour and availability.'
                      : 'Define a shift that can be assigned in the roster.'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background hover:text-text"
                aria-label="Close"
              >
                <X size={17} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="max-h-[calc(90vh-3.5rem)] space-y-3.5 overflow-y-auto p-4"
            >
              {/* Name + code */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Shift Name *</label>
                  <input
                    required
                    autoFocus
                    value={form.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g. Morning Shift"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Shift Code *</label>
                  <div className="flex items-center gap-1.5">
                    <input
                      required
                      value={form.shiftCode}
                      onChange={(e) => {
                        setForm({ ...form, shiftCode: e.target.value });
                        setCodeEdited(true);
                      }}
                      placeholder="Auto-generated"
                      className={`${inputClass} font-mono`}
                    />
                    <button
                      type="button"
                      onClick={regenerateCode}
                      disabled={!form.name}
                      title="Regenerate from name"
                      className="shrink-0 rounded-xl border border-border p-2 text-text-muted transition-colors hover:bg-background hover:text-primary disabled:opacity-40"
                    >
                      <RefreshCw size={15} />
                    </button>
                  </div>
                  <p className="mt-1 text-[11px] text-text-muted">
                    {codeEdited ? 'Custom code' : 'Auto-generated from name'}
                  </p>
                </div>
              </div>

              {/* Timing */}
              <div className="space-y-2.5 rounded-xl border border-border/70 bg-background/50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-text">
                    <Clock size={13} className="text-primary" />
                    Shift hours
                  </span>
                  <div className="flex items-center gap-1.5">
                    {isOvernight && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600">
                        <Moon size={10} />
                        Overnight
                      </span>
                    )}
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                      {duration}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={labelClass}>Start Time *</label>
                    <TimePicker
                      required
                      value={form.startTime}
                      onChange={(value) => setForm({ ...form, startTime: value })}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>End Time *</label>
                    <TimePicker
                      required
                      value={form.endTime}
                      onChange={(value) => setForm({ ...form, endTime: value })}
                      className={inputClass}
                    />
                  </div>
                </div>
                <p className="text-[11px] text-text-muted">
                  End time at or before start time is treated as an overnight shift.
                </p>
              </div>

              {/* Break + order */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Break (minutes)</label>
                  <input
                    type="number"
                    min={0}
                    value={form.breakMinutes}
                    onChange={(e) => setForm({ ...form, breakMinutes: Number(e.target.value) })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Display Order</label>
                  <input
                    type="number"
                    value={form.displayOrder}
                    onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) })}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Colour */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-medium text-text">Colour</label>
                  <span className="font-mono text-[11px] text-text-muted">{form.color}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {SHIFT_COLORS.map((color) => {
                    const selected = form.color.toLowerCase() === color.toLowerCase();
                    return (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setForm({ ...form, color })}
                        aria-label={`Use ${color}`}
                        aria-pressed={selected}
                        className={`flex h-7 w-7 items-center justify-center rounded-full border-2 transition-transform ${
                          selected
                            ? 'scale-110 border-text'
                            : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                      >
                        {selected && <Check size={12} className="text-white drop-shadow" />}
                      </button>
                    );
                  })}
                  <label
                    className="relative flex h-7 w-7 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-dashed border-border bg-background text-text-muted transition-colors hover:border-primary/40 hover:text-primary"
                    title="Custom colour"
                  >
                    <Plus size={13} />
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className="absolute inset-0 cursor-pointer opacity-0"
                    />
                  </label>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className={labelClass}>
                  Description <span className="font-normal text-text-muted">(optional)</span>
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Add a short note about this shift"
                  rows={2}
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* Active */}
              <div className="flex items-center justify-between gap-3 rounded-xl border border-border/70 bg-background/50 px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-text">Active</p>
                  <p className="text-[11px] text-text-muted">Available for assignment in the roster.</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.isActive}
                  onClick={() => setForm({ ...form, isActive: !form.isActive })}
                  className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
                    form.isActive ? 'bg-primary' : 'bg-border'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                      form.isActive ? 'left-[1.125rem]' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-xl px-3 py-2 text-xs font-medium text-text-muted transition-colors hover:bg-background hover:text-text"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition-all hover:bg-primary-dark disabled:opacity-50"
                >
                  {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {editing ? 'Save Changes' : 'Create Shift'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete shift?"
        description={
          <>
            <span className="font-medium text-text">{pendingDelete?.name}</span> will be removed from the shift
            master. Existing roster entries keep their hours but lose the shift link.
          </>
        }
        confirmLabel="Delete"
        loading={deleteShift.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
